import { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  whatsNewEntries,
  whatsNewText,
  type WhatsNewEntry as WhatsNewEntryData,
} from '../data/whatsNew';
import { formatDay, formatDayTime } from '../../../utils/datetime';

/* The newest entries of whatsNew.txt — few enough that the card stays
   the height of the ones beside it. It used to show seven and scroll,
   which hid the older ones behind a bar nobody looks for; they are now
   a click away on a page of their own instead. */
const WHATS_NEW_COUNT = 4;

/*
 * One entry, three lines tall until asked: the text clamps at three
 * rendered lines (`.overview-clamped`), and a "more …" behind it
 * unfolds the rest. The offer only appears where the clamp really cuts
 * something off — measured, because a "more" on a short entry would
 * promise more than there is. The measurement follows the card's
 * width, which the masonry layout changes with the window.
 *
 * Whether it stands unfolded is the list's business, not its own: only
 * one entry is open at a time (see WhatsNewList).
 */
function WhatsNewEntry({
  entry,
  expanded,
  onToggle,
}: {
  entry: WhatsNewEntryData;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [clipped, setClipped] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element || expanded) return undefined;

    const measure = () =>
      setClipped(element.scrollHeight > element.clientHeight + 1);
    measure();

    /* jsdom has no ResizeObserver; there the first measurement stands */
    if (typeof ResizeObserver !== 'function') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [expanded, entry]);

  /* something to fold or unfold at all: a short entry stays plain text */
  const toggleable = clipped || expanded;

  return (
    /* the line names the day; the exact moment — the last commit of
       the topic, in the timezone of whoever reads it — is one hover
       away rather than in the way */
    <li title={formatDayTime(entry.at, i18n.language)}>
      {/*
       * The text is the switch as well. The "…" a clamped entry ends
       * with is drawn by the browser, not by us — it is part of the
       * text and answers no click, which is exactly where one lands.
       * So the whole text takes the click.
       *
       * It stays a plain div rather than becoming a button: the named
       * control below is the one for the keyboard and the screen
       * reader, and a second tab stop saying the same thing would only
       * lengthen the way through the card.
       */}
      <div
        ref={textRef}
        className={[
          expanded ? null : 'overview-clamped',
          toggleable ? 'is-clamp-toggle' : null,
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={toggleable ? onToggle : undefined}
      >
        <span className="overview-muted">
          {formatDay(entry.date, i18n.language)}
        </span>{' '}
        {whatsNewText(entry, i18n.resolvedLanguage ?? i18n.language)}
      </div>
      {toggleable && (
        <button
          type="button"
          className="overview-clamp-toggle"
          onClick={onToggle}
        >
          {expanded ? t('overview.whatsNew.less') : t('overview.whatsNew.more')}
        </button>
      )}
    </li>
  );
}

/*
 * The news itself, without the box around it: the dashboard card and
 * the dialog that greets a freshly loaded build show the same list, so
 * there is one place where it is written.
 *
 * `onFollowMore` lets the dialog close itself when the reader leaves
 * for the full page — a modal that stays open over the page it just
 * led to would have to be dismissed twice.
 *
 * What is passed as children shares the closing line with the link:
 * the dialog puts its close button there. Two ways out of the same
 * window belong on one line — below one another they read as two
 * separate offers and make the foot of the dialog restless.
 */
export function WhatsNewList({
  onFollowMore,
  children,
}: {
  onFollowMore?: () => void;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  /*
   * One entry unfolded at a time — the moment of the one that is open,
   * or null while all of them are short. Several open at once would
   * push the rest of the card, and the cards below it in the column,
   * out of sight; and whoever opens the next one has read the last.
   * Clicking the open entry again folds it back, so the card can also
   * be left as it was found.
   */
  const [openAt, setOpenAt] = useState<string | null>(null);

  return (
    <>
      <ul className="overview-list">
        {whatsNewEntries.slice(0, WHATS_NEW_COUNT).map((entry) => (
          <WhatsNewEntry
            key={entry.at}
            entry={entry}
            expanded={openAt === entry.at}
            onToggle={() =>
              setOpenAt((current) => (current === entry.at ? null : entry.at))
            }
          />
        ))}
      </ul>
      {/* what lies behind the link is not more about what stands here
          but what stood here before it, so that is what it says */}
      <p className="overview-more">
        {children}
        <Link to="/dashboard/whats-new" onClick={onFollowMore}>
          {t('overview.whatsNew.older')}
        </Link>
      </p>
    </>
  );
}
